import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import {
  ShoppingBag,
  Link as LinkIcon,
  Unlink,
  RefreshCw,
  ArrowUpRight,
  Check,
  X,
  AlertCircle,
  Package,
  MapPin,
  Upload,
  ChevronRight,
  Store,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAdminId } from '../utils/getAdminId';

interface ShopifyShop {
  id: string;
  shopDomain: string;
  scope: string;
  installedAt: string;
}

interface ShopifyProduct {
  id: string;
  title: string;
  variants: {
    id: string;
    title: string;
    sku: string;
    inventoryItemId: string;
    inventoryLevels: {
      locationId: string;
      locationName: string;
      available: number;
    }[];
  }[];
}

interface ShopifyLocation {
  id: string;
  name: string;
  isActive: boolean;
}

interface ProductMapping {
  id: string;
  productId: string;
  shopifyProductId: string;
  shopifyVariantId: string;
  shopifyInventoryItemId: string;
}

interface LocalProduct {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  category: string;
}

export const ShopifyIntegration: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [shop, setShop] = useState<ShopifyShop | null>(null);
  const [shopDomain, setShopDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'overview' | 'mapping' | 'sync'>('overview');
  const [shopifyProducts, setShopifyProducts] = useState<ShopifyProduct[]>([]);
  const [locations, setLocations] = useState<ShopifyLocation[]>([]);
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [localProducts, setLocalProducts] = useState<LocalProduct[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [credentialsNotConfigured, setCredentialsNotConfigured] = useState(false);

  const [mappingModal, setMappingModal] = useState<{
    open: boolean;
    localProduct: LocalProduct | null;
  }>({ open: false, localProduct: null });
  const [selectedShopifyVariant, setSelectedShopifyVariant] = useState<{
    productId: string;
    variantId: string;
    inventoryItemId: string;
  } | null>(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth/status`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        console.error('Status fetch failed:', response.status);
        setShop(null);
        return;
      }

      const data = await response.json();

      if (data.connected && data.shops?.length > 0) {
        setShop(data.shops[0]);
      } else {
        setShop(null);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
      setShop(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchShopifyData = useCallback(async () => {
    if (!shop) return;

    setLoadingProducts(true);
    try {
      const [productsRes, locationsRes, mappingsRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/products`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/locations`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/mappings`, {
          headers: getAuthHeaders(),
        }),
      ]);

      const [productsData, locationsData, mappingsData] = await Promise.all([
        productsRes.json(),
        locationsRes.json(),
        mappingsRes.json(),
      ]);

      setShopifyProducts(productsData.products || []);
      setLocations(locationsData.locations || []);
      setMappings(mappingsData.mappings || []);

      if (locationsData.locations?.length > 0 && !selectedLocation) {
        setSelectedLocation(locationsData.locations[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch Shopify data:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [shop, getAuthHeaders, selectedLocation]);

  const fetchLocalProducts = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId(user);

    const { data, error } = await supabase
      .from('products')
      .select('id, name, sku, quantity, category')
      .eq('admin_id', adminId)
      .order('name');

    if (!error && data) {
      setLocalProducts(data);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (shop) {
      fetchShopifyData();
      fetchLocalProducts();
    }
  }, [shop, fetchShopifyData, fetchLocalProducts]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SHOPIFY_OAUTH_SUCCESS') {
        setSuccess('Shopify store connected successfully!');
        fetchStatus();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchStatus]);

  const handleConnect = async () => {
    let cleanedDomain = shopDomain.trim().toLowerCase();

    if (!cleanedDomain) {
      setError('Please enter your Shopify store domain');
      return;
    }

    cleanedDomain = cleanedDomain
      .replace(/^https?:\/\//, '')
      .replace(/\.myshopify\.com.*$/, '')
      .replace(/[^a-z0-9-]/g, '');

    if (!cleanedDomain || cleanedDomain.length < 2) {
      setError('Please enter a valid Shopify store name');
      return;
    }

    setConnecting(true);
    setError(null);
    setCredentialsNotConfigured(false);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth/start?shop=${encodeURIComponent(cleanedDomain)}`,
        { headers: getAuthHeaders() }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.credentialsNotConfigured) {
          setCredentialsNotConfigured(true);
          setError(data.error);
          return;
        }
        throw new Error(data.error || 'Failed to start OAuth');
      }

      const popup = window.open(
        data.installUrl,
        'shopify-oauth',
        'width=600,height=700,scrollbars=yes'
      );

      if (!popup) {
        window.location.href = data.installUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!shop || !confirm('Are you sure you want to disconnect this Shopify store?')) return;

    setDisconnecting(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth/disconnect`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ shopId: shop.id }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }

      setShop(null);
      setSuccess('Shopify store disconnected');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleMapProduct = async () => {
    if (!mappingModal.localProduct || !selectedShopifyVariant) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/map`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            productId: mappingModal.localProduct.id,
            shopifyProductId: selectedShopifyVariant.productId,
            shopifyVariantId: selectedShopifyVariant.variantId,
            shopifyInventoryItemId: selectedShopifyVariant.inventoryItemId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to map product');
      }

      setSuccess('Product mapped successfully!');
      setMappingModal({ open: false, localProduct: null });
      setSelectedShopifyVariant(null);
      fetchShopifyData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to map product');
    }
  };

  const handlePushInventory = async (productId: string) => {
    setPushing(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/push`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ productId, locationId: selectedLocation }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to push inventory');
      }

      setSuccess(`Inventory pushed: ${data.pushed.quantity} units`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to push inventory');
    } finally {
      setPushing(false);
    }
  };

  const handlePushAll = async () => {
    if (!confirm('Push all product quantities to Shopify? This will match products by SKU and create mappings automatically.')) return;

    setPushing(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/push-all`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ locationId: selectedLocation }),
          signal: AbortSignal.timeout(150000),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to push inventory');
      }

      const summary = data.summary || {};
      setSuccess(`Push complete: ${summary.verified || 0} synced, ${summary.skipped || 0} skipped, ${summary.failed || 0} failed`);
      await fetchShopifyData();
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Request timed out. The server may still be processing. Try refreshing in a minute.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to push inventory');
      }
    } finally {
      setPushing(false);
    }
  };

  const getMappingForProduct = (productId: string) => {
    return mappings.find(m => m.productId === productId);
  };

  const filteredLocalProducts = localProducts.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Only administrators can manage Shopify integration.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <ShoppingBag className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Shopify Integration
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Connect your Shopify store to sync inventory
            </p>
          </div>
        </div>
      </div>

      {error && credentialsNotConfigured && (
        <div className="mb-6 p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
                Shopify Credentials Required
              </h3>
              <p className="text-amber-700 dark:text-amber-300 mb-4">
                {error}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>What you need to do:</strong>
                </p>
                <ol className="text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside space-y-1">
                  <li>Contact your Super Admin</li>
                  <li>Request Shopify app credentials to be configured for your account</li>
                  <li>Once configured, return here to connect your store</li>
                </ol>
              </div>
            </div>
            <button onClick={() => { setError(null); setCredentialsNotConfigured(false); }} className="flex-shrink-0">
              <X className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </button>
          </div>
        </div>
      )}

      {error && !credentialsNotConfigured && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-green-700 dark:text-green-300">{success}</p>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-4 w-4 text-green-600" />
          </button>
        </div>
      )}

      {!shop ? (
        <div className="card p-8">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Store className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Connect Your Shopify Store
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Link your Shopify store to automatically sync inventory levels between GetInv and your online store.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                  Shopify Store Domain
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shopDomain}
                    onChange={(e) => setShopDomain(e.target.value)}
                    placeholder="your-store"
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <span className="flex items-center px-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm">
                    .myshopify.com
                  </span>
                </div>
              </div>

              <Button
                onClick={handleConnect}
                loading={connecting}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Connect Shopify Store
              </Button>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What you can do after connecting:
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 text-left">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Sync inventory levels automatically
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Push stock updates from GetInv to Shopify
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Receive Shopify stock changes in real-time
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Map products between systems
                </li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Store className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {shop.shopDomain}
                    </h2>
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                      Connected
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Connected {new Date(shop.installedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={fetchShopifyData}
                  disabled={loadingProducts}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingProducts ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDisconnect}
                  loading={disconnecting}
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            </div>
          </div>

          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex gap-6">
              {[
                { id: 'overview', label: 'Overview', icon: Store },
                { id: 'mapping', label: 'Product Mapping', icon: LinkIcon },
                { id: 'sync', label: 'Sync Inventory', icon: Upload },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/settings/shopify-sync"
                  className="block card p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                        <Zap className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Inventory Sync
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Pull and push inventory between GetInv and Shopify
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </Link>

                <Link
                  to="/shopify/orders"
                  className="block card p-6 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                        <ShoppingBag className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Shopify Orders
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          View and manage orders from your Shopify store
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Shopify Products</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {shopifyProducts.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <LinkIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Mapped Products</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {mappings.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Locations</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {locations.length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mapping' && (
            <div className="card">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Product Mapping
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Link your local products to Shopify variants for inventory sync
                    </p>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search products..."
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredLocalProducts.map((product) => {
                  const mapping = getMappingForProduct(product.id);
                  const shopifyProduct = mapping
                    ? shopifyProducts.find(p => p.id === mapping.shopifyProductId)
                    : null;
                  const shopifyVariant = shopifyProduct?.variants.find(v => v.id === mapping?.shopifyVariantId);

                  return (
                    <div key={product.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{product.name}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                            <span>SKU: {product.sku || 'N/A'}</span>
                            <span>Qty: {product.quantity}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {mapping ? (
                          <>
                            <div className="text-right">
                              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                <CheckCircle2 className="h-4 w-4" />
                                <span className="text-sm font-medium">Mapped</span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {shopifyProduct?.title} - {shopifyVariant?.title}
                              </p>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setMappingModal({ open: true, localProduct: product })}
                          >
                            <LinkIcon className="h-4 w-4 mr-1" />
                            Map to Shopify
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {filteredLocalProducts.length === 0 && (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No products found
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-6">
              <div className="card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Push Inventory to Shopify
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Send your current inventory levels to Shopify
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                    >
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                    <Button
                      onClick={handlePushAll}
                      loading={pushing}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Push All Products
                    </Button>
                  </div>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                  {localProducts.filter(p => getMappingForProduct(p.id)).map((product) => {
                    const mapping = getMappingForProduct(product.id);
                    const shopifyProduct = shopifyProducts.find(p => p.id === mapping?.shopifyProductId);
                    const shopifyVariant = shopifyProduct?.variants.find(v => v.id === mapping?.shopifyVariantId);
                    const shopifyQty = shopifyVariant?.inventoryLevels?.[0]?.available ?? 'N/A';

                    return (
                      <div key={product.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">{product.name}</p>
                            <p className="text-sm text-gray-500">SKU: {product.sku || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">GetInv</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{product.quantity}</p>
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-gray-400" />
                          <div className="text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Shopify</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{shopifyQty}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handlePushInventory(product.id)}
                            disabled={pushing}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            Push
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {mappings.length === 0 && (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      No mapped products. Go to Product Mapping to link your products first.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {mappingModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Map to Shopify Product
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Select a Shopify variant for "{mappingModal.localProduct?.name}"
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMappingModal({ open: false, localProduct: null });
                    setSelectedShopifyVariant(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <div className="space-y-4">
                {shopifyProducts.map((product) => (
                  <div key={product.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 font-medium text-gray-900 dark:text-gray-100">
                      {product.title}
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {product.variants.map((variant) => (
                        <label
                          key={variant.id}
                          className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                            selectedShopifyVariant?.variantId === variant.id
                              ? 'bg-cyan-50 dark:bg-cyan-900/20'
                              : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="shopifyVariant"
                            checked={selectedShopifyVariant?.variantId === variant.id}
                            onChange={() => setSelectedShopifyVariant({
                              productId: product.id,
                              variantId: variant.id,
                              inventoryItemId: variant.inventoryItemId,
                            })}
                            className="h-4 w-4 text-cyan-600"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {variant.title === 'Default Title' ? 'Default' : variant.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              SKU: {variant.sku || 'N/A'} | Stock: {variant.inventoryLevels?.[0]?.available ?? 'N/A'}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setMappingModal({ open: false, localProduct: null });
                  setSelectedShopifyVariant(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMapProduct}
                disabled={!selectedShopifyVariant}
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Map Product
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
